import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLangPrefix, withLangPrefix } from '../../hooks/useLangPrefix';
import { ChevronRight, LogOut, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import { UserMenu } from './UserMenu';

const HOURS_GREETING = () => {
  const h = new Date().getHours();
  if (h < 6) return 'Bonne nuit';
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bel après-midi';
  return 'Bonsoir';
};

/**
 * DashboardShell — premium left-sidebar layout for the student / affiliate
 * / admin back-offices. Provides a personalized welcome header and a slot
 * for the active section. `sections` is an array of:
 *   { key, label, Icon, badge?, content: <ReactNode> }
 */
export const DashboardShell = ({
  // Top hero
  kicker = 'ESPACE PERSONNEL',
  roleLabel = 'Espace',
  // Sidebar
  sections,
  defaultSection,
  // Controlled mode (used by AdminDashboard which keeps its own state)
  activeKey,
  onSectionChange,
  // Optional: extra block under the welcome card (e.g. share code for affiliate)
  topSlot = null,
  // Optional: floating action area inside the hero band top-right (notification bell, etc.)
  headerSlot = null,
  testIdRoot = 'dashboard',
  // When true, the dashboard is rendered INSIDE the public Layout (which
  // already provides a fixed Navbar). We then hide the redundant internal
  // topbar and push content below the public Navbar.
  nested = false,
  children,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang } = useLangPrefix();
  const [internal, setInternal] = useState(defaultSection || sections[0]?.key);
  const active = activeKey !== undefined ? activeKey : internal;
  const setActive = (key) => {
    if (onSectionChange) onSectionChange(key);
    if (activeKey === undefined) setInternal(key);
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('À bientôt !');
    navigate(withLangPrefix('/', lang));
  };

  const activeSection = sections.find((s) => s.key === active);

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid={testIdRoot}>
      {/* Compact top bar — logo + greeting + notification slot + user menu */}
      {!nested && (
      <div className="bg-[#141C2E] text-white border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between gap-4">
          <Link
            to={withLangPrefix('/', lang)}
            className="inline-flex items-center gap-2 text-white/85 hover:text-[#C9B896] transition-colors shrink-0"
            data-testid={`${testIdRoot}-topbar-logo`}
          >
            <span className="font-serif italic text-lg text-[#C9B896] leading-none">Sellerie</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 leading-none hidden sm:inline">Académie</span>
          </Link>

          {/* Greeting (replaces the old huge hero band) */}
          <div className="hidden md:flex items-baseline gap-2 flex-1 min-w-0 px-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#C9B896]">{kicker}</span>
            <span className="text-white/30 text-xs">·</span>
            <span className="font-serif italic text-base text-white truncate">
              {HOURS_GREETING()}, <span className="text-[#C9B896]">{user?.first_name || 'cher membre'}.</span>
            </span>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {headerSlot && (
              <div data-testid={`${testIdRoot}-header-slot`}>
                {headerSlot}
              </div>
            )}
            <UserMenu onLogout={handleLogout} homeHref={withLangPrefix('/', lang)} />
          </div>
        </div>
      </div>
      )}

      {/* Personalized welcome banner — shown only when nested inside the public Layout */}
      {nested && (
        <div className="bg-gradient-to-br from-[#1F2A4A] via-[#243259] to-[#1F2A4A] text-white pt-32 lg:pt-36 pb-6" data-testid={`${testIdRoot}-welcome-banner`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-baseline justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-1">{kicker}</p>
              <h1 className="font-serif italic text-2xl md:text-3xl text-white truncate">
                {HOURS_GREETING()},{' '}
                <span className="text-[#C9B896] not-italic font-light">{user?.first_name || 'cher membre'}.</span>
              </h1>
            </div>
            {headerSlot && (
              <div data-testid={`${testIdRoot}-header-slot`} className="shrink-0">
                {headerSlot}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className={`max-w-7xl mx-auto px-6 md:px-12 relative pb-20 ${nested ? 'pt-8 md:pt-10' : 'pt-8 md:pt-12'}`}>
        {/* Mobile section toggle */}
        <button
          onClick={() => setMobileMenuOpen((o) => !o)}
          className="lg:hidden mb-4 w-full flex items-center justify-between bg-white border border-[#1F2A4A]/15 rounded-xl px-5 py-4 shadow-sm"
          data-testid={`${testIdRoot}-mobile-toggle`}
        >
          <span className="flex items-center gap-3 text-[#1F2A4A] font-medium">
            {activeSection?.Icon && <activeSection.Icon className="w-4 h-4 text-[#C9B896]" />}
            {activeSection?.label}
          </span>
          {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6 md:gap-8">
          {/* SIDEBAR */}
          <aside
            className={`lg:sticky ${nested ? 'lg:top-32' : 'lg:top-20'} lg:self-start bg-white border border-[#1F2A4A]/10 rounded-2xl shadow-[0_20px_50px_-25px_rgba(31,42,74,0.18)] overflow-hidden ${
              mobileMenuOpen ? 'block' : 'hidden lg:block'
            }`}
            data-testid={`${testIdRoot}-sidebar`}
          >
            {/* Top slot (optional) */}
            {topSlot && (
              <div className="p-5 border-b border-[#1F2A4A]/8 bg-[#FAF7F0]/50">
                {topSlot}
              </div>
            )}

            <nav className="p-3 space-y-1">
              {sections.map((s) => {
                const Icon = s.Icon;
                const isActive = active === s.key;
                const handleClick = () => {
                  if (s.externalPath) {
                    setMobileMenuOpen(false);
                    navigate(withLangPrefix(s.externalPath, lang));
                    return;
                  }
                  setActive(s.key);
                  setMobileMenuOpen(false);
                };
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={handleClick}
                    className={`group w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-300 ${
                      isActive
                        ? 'bg-[#1F2A4A] text-white shadow-md shadow-[#1F2A4A]/15'
                        : 'text-[#1F2A4A]/85 hover:bg-[#C9B896]/15 hover:text-[#1F2A4A]'
                    }`}
                    data-testid={`${testIdRoot}-nav-${s.key}`}
                  >
                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive ? 'bg-[#C9B896]/25 text-[#C9B896]' : 'bg-[#C9B896]/12 text-[#1F2A4A] group-hover:bg-[#C9B896]/25'
                    }`}>
                      <Icon className="w-4 h-4" strokeWidth={1.6} />
                    </span>
                    <span className="flex-1 text-sm font-medium">{s.label}</span>
                    {s.badge != null && (
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
                        isActive ? 'bg-white/15 text-white' : 'bg-[#C9B896]/15 text-[#1F2A4A]'
                      }`}>
                        {s.badge}
                      </span>
                    )}
                    {isActive && <ChevronRight className="w-3.5 h-3.5 text-[#C9B896]" />}
                  </button>
                );
              })}
            </nav>

            {/* Bottom logout */}
            <div className="p-3 border-t border-[#1F2A4A]/8">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                data-testid={`${testIdRoot}-logout`}
              >
                <span className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <LogOut className="w-4 h-4" strokeWidth={1.6} />
                </span>
                <span className="text-sm font-medium">Se déconnecter</span>
              </button>
            </div>
          </aside>

          {/* CONTENT */}
          <main className="min-w-0" data-testid={`${testIdRoot}-content`}>
            {activeSection?.content ?? children}
          </main>
        </div>
      </div>
    </div>
  );
};
